// Presentation only. These groupings never change a Delivery lifecycle state.
//
// The overview separates three things that used to share one count:
//   decisions - a queue where a person records an outcome the server acts on
//   watch     - work that may need a look but has no decision of its own
//   recent    - signals kept for awareness, with no resolution state at all
//
// An ordinary finalized proof is in none of them. Delivery is already complete
// when the proof is finalized, so only a proof the server flags for review is
// a decision.
const rows = (value) => Array.isArray(value) ? value : Array.isArray(value?.items) ? value.items : [];

// Each queue is read as one page. A page that comes back full may have more
// behind it, so its length is a minimum rather than a total.
export const QUEUE_PAGE_LIMITS = Object.freeze({
  failedStops: 100, assignmentProblems: 50, returns: 50, proofs: 25, riderReports: 100, fulfillment: 100,
});

const label = (item) => item.orderReference || item.riderName
  || (item.shipmentId ? `Parcel #${item.shipmentId}` : null)
  || (item.returnSessionId ? `Return #${item.returnSessionId}` : null);

function entry(key, title, detail, items, to, limit) {
  return {
    key, title, detail, to,
    count: items.length,
    atLeast: Boolean(limit) && items.length >= limit,
    examples: items.map(label).filter(Boolean).slice(0, 3),
  };
}

export function logisticsBriefing({ board = {}, assignmentProblems = [], failedStops = [],
  riderReports = [], returnSessions = [], returnedParcels = [], cancellations = [], proofs = [],
  fulfillmentBlocked = 0 } = {}) {
  const decisions = [];
  const watch = [];
  const recent = [];
  const add = (list, item) => { if (item.count > 0) list.push(item); };

  if (Number(fulfillmentBlocked) > 0) {
    decisions.push({
      key: 'fulfillment', title: 'Orders held before dispatch',
      detail: 'Each one says why it stopped and where to resolve it.',
      to: '/business/orders/fulfillment?group=NEEDS_ATTENTION',
      count: Number(fulfillmentBlocked), atLeast: Number(fulfillmentBlocked) >= QUEUE_PAGE_LIMITS.fulfillment, examples: [],
    });
  }
  add(decisions, entry('failed', 'Failed stops waiting for a decision',
    'Choose what happens to each parcel next. It stays with the rider until you do.',
    rows(failedStops), '/business/delivery?view=exceptions', QUEUE_PAGE_LIMITS.failedStops));
  add(decisions, entry('assignment', 'Riders reported a problem with assigned work',
    'Keep the delivery with the rider or reassign it.',
    rows(assignmentProblems).filter((item) => item.status === 'OPEN'),
    '/business/delivery?view=issues', QUEUE_PAGE_LIMITS.assignmentProblems));
  add(decisions, entry('returns', 'Rider returns to process at the counter',
    'Confirm what came back, then decide what happens to each parcel.',
    [...rows(returnSessions), ...rows(returnedParcels), ...rows(cancellations)],
    '/business/delivery?view=returns', QUEUE_PAGE_LIMITS.returns));
  add(decisions, entry('proofs', 'Delivery proofs flagged for review',
    'The recorded location did not match the delivery address closely enough.',
    rows(proofs).filter((item) => item.reviewRequired),
    '/business/delivery/proofs', QUEUE_PAGE_LIMITS.proofs));

  add(watch, entry('interrupted', 'Interrupted trips',
    'Check the trip and its parcels before the rider continues.',
    rows(board.items).filter((run) => run.status === 'INTERRUPTED')
      .map((run) => ({ riderName: run.rider?.displayName || `Trip #${run.id}` })),
    '/business/delivery?view=trips&status=INTERRUPTED'));

  add(recent, entry('reports', 'Road reports from riders',
    'For awareness. A report does not change any delivery and has nothing to resolve.',
    rows(riderReports), '/business/delivery?view=reports', QUEUE_PAGE_LIMITS.riderReports));

  return { decisions, watch, recent };
}
